import globalWindow from '@shared/core/globals';
import { useResponsive } from '../hooks/useResponsive';
import type { MediaScreen } from './DefaultMediaMatchProvider';
import { useDefaultMediaMatchContext } from './DefaultMediaMatchProvider';

/**
 * Media screen detection utilities based on responsive breakpoints.
 *
 * Breakpoint system — `mobile`/`tablet`/`desktop` resolve through JoyKit's
 * `breakpointsByScreen`, i.e. `theme.breakpoints[1]` and `[2]`. With the default
 * `defaultBreakpointWidths` ([480, 768, 1024, 1440, 1920]) that means:
 * - Mobile: < 768px
 * - Tablet: ≥ 768px and < 1024px
 * - Desktop: ≥ 1024px
 *
 * Note: these are the widths the hooks actually classify on, not the whole
 * `defaultBreakpointWidths` array — only indexes 1 and 2 take part. An earlier
 * version of this comment claimed 1024/1280 thresholds; that has misled reviews
 * of code calling `useIsMobileScreen`, so keep it in step with
 * `createBreakpoints` if the widths ever change.
 */

const mediaScreenPrecedence: { [K in MediaScreen]: number } = {
  mobile: 0,
  tablet: 1,
  desktop: 2,
};

export interface UseMediaScreenParams {
  minScreenSize?: MediaScreen;
  maxScreenSize?: MediaScreen;
}

export const useMediaScreen = ({ minScreenSize = 'mobile', maxScreenSize = 'desktop' }: UseMediaScreenParams): boolean => {
  const defaultScreen = useDefaultMediaMatchContext()?.defaultScreen;
  const [mediaScreen] = useResponsive<MediaScreen>({ values: { mobile: 'mobile', tablet: 'tablet', desktop: 'desktop' } }, defaultScreen || 'desktop');
  const currentMediaScreenPrecedence = mediaScreenPrecedence[mediaScreen];

  return mediaScreenPrecedence[minScreenSize] <= currentMediaScreenPrecedence && currentMediaScreenPrecedence <= mediaScreenPrecedence[maxScreenSize];
};

/**
 * Hook that returns true when the current screen size is mobile.
 *
 * @returns {boolean} True when screen width is < 768px
 */
export const useIsMobileScreen = () => {
  return useMediaScreen({
    minScreenSize: 'mobile',
    maxScreenSize: 'mobile',
  });
};

/**
 * Hook that returns true when the current screen size is tablet.
 *
 * @returns {boolean} True when screen width is ≥ 768px and < 1024px
 */
export const useIsTabletScreen = () => {
  return useMediaScreen({
    minScreenSize: 'tablet',
    maxScreenSize: 'tablet',
  });
};

/**
 * Hook that returns true when the current screen size is mobile or tablet.
 *
 * @returns {boolean} True when screen width is < 1024px (mobile: < 768px, tablet: ≥ 768px and < 1024px)
 */
export const useIsMobileOrTablet = () => {
  return useMediaScreen({
    minScreenSize: 'mobile',
    maxScreenSize: 'tablet',
  });
};

export const useIsIOS = (): boolean => (globalWindow.navigator ? Boolean(navigator?.userAgent.match(/ipad|iphone/i)) : false);
